<script setup>
/**
 * Login method.
 *
 * Archetype G: the account's sign-in method is one exclusive choice, so it is
 * two radio cards rather than a switch buried in a section header. Picking
 * `Email and password` back off SAML is the same destructive action the switch
 * used to be - it deletes the stored SAML settings - and picking SAML reveals
 * the identity-provider form below the cards.
 */
import { ref, computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import samlSettingsAPI from 'dashboard/api/samlSettings';

import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsChoiceCard from 'dashboard/components-next/Settings/SettingsChoiceCard.vue';
import SettingsFieldGrid from 'dashboard/components-next/Settings/SettingsFieldGrid.vue';
import SettingsField from 'dashboard/components-next/Settings/SettingsField.vue';
import TextInput from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import NextButton from 'next/button/Button.vue';
import SamlInfoSection from './SamlInfoSection.vue';
import SamlAttributeMap from './SamlAttributeMap.vue';

const LOGIN_METHODS = {
  PASSWORD: 'password',
  SAML: 'saml',
};

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const id = ref(null);
const fingerprint = ref('');
const spEntityId = ref('');
const isEnabled = ref(false);
const isSubmitting = ref(false);
const isLoading = ref(true);

const formState = reactive({
  ssoUrl: '',
  certificate: '',
  idpEntityId: '',
});

const validations = {
  ssoUrl: { required },
  certificate: { required },
  idpEntityId: { required },
};

const v$ = useVuelidate(validations, formState);

const hasFeature = computed(() => isCloudFeatureEnabled('saml'));

const ssoUrlError = computed(() =>
  v$.value.ssoUrl.$error
    ? t('SECURITY_SETTINGS.SAML.VALIDATION.SSO_URL_ERROR')
    : ''
);

const certificateError = computed(() =>
  v$.value.certificate.$error
    ? t('SECURITY_SETTINGS.SAML.VALIDATION.CERTIFICATE_ERROR')
    : ''
);

const idpEntityIdError = computed(() =>
  v$.value.idpEntityId.$error
    ? t('SECURITY_SETTINGS.SAML.VALIDATION.IDP_ENTITY_ID_ERROR')
    : ''
);

const loadSamlSettings = async () => {
  if (!hasFeature.value) return;

  try {
    isLoading.value = true;
    const response = await samlSettingsAPI.get();
    const settings = response.data;

    if (settings.sso_url) {
      id.value = settings.id;
      formState.ssoUrl = settings.sso_url;
      formState.certificate = settings.certificate || '';
      spEntityId.value = settings.sp_entity_id || '';
      formState.idpEntityId = settings.idp_entity_id || '';
      fingerprint.value = settings.fingerprint || '';
      isEnabled.value = formState.ssoUrl !== '';
    }
  } catch (error) {
    // If no settings exist (404), that's expected - just keep defaults
    if (error.response?.status !== 404) {
      useAlert(t('SECURITY_SETTINGS.SAML.API.ERROR_LOADING'));
    }
  } finally {
    isLoading.value = false;
  }
};

const saveSamlSettings = async settings => {
  try {
    isSubmitting.value = true;

    if (isEnabled.value && formState.ssoUrl) {
      // Create or update settings based on existing id
      let response;
      if (id.value) {
        response = await samlSettingsAPI.update(settings);
      } else {
        response = await samlSettingsAPI.create(settings);
      }

      // Update local state with response data including fingerprint and id
      if (response?.data) {
        id.value = response.data.id;
        fingerprint.value = response.data.fingerprint || '';
        spEntityId.value = response.data.sp_entity_id || '';
      }

      useAlert(t('SECURITY_SETTINGS.SAML.API.SUCCESS'));
    } else {
      // Disable/delete settings
      await samlSettingsAPI.delete();
      useAlert(t('SECURITY_SETTINGS.SAML.API.DISABLED'));
    }
  } catch (error) {
    // Handle backend validation errors
    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors;
      const firstError = Array.isArray(errorMessages)
        ? errorMessages[0]
        : errorMessages;
      useAlert(firstError);
    } else {
      useAlert(t('SECURITY_SETTINGS.SAML.API.ERROR'));
    }
    throw error;
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const settings = {
    sso_url: formState.ssoUrl,
    certificate: formState.certificate,
    idp_entity_id: formState.idpEntityId,
    role_mappings: {},
  };

  await saveSamlSettings(settings);
};

const handleDisable = async () => {
  id.value = null;
  formState.ssoUrl = '';
  formState.certificate = '';
  spEntityId.value = '';
  formState.idpEntityId = '';
  fingerprint.value = '';

  // the empty save will delete the SAML settings item
  await saveSamlSettings({});
};

const loginMethod = computed({
  get: () => (isEnabled.value ? LOGIN_METHODS.SAML : LOGIN_METHODS.PASSWORD),
  set: async value => {
    isEnabled.value = value === LOGIN_METHODS.SAML;
    if (!isEnabled.value) await handleDisable();
  },
});

onMounted(() => {
  loadSamlSettings();
});
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <SettingsSection
      flush
      :title="t('SECURITY_SETTINGS.LOGIN_METHOD.TITLE')"
      :description="t('SECURITY_SETTINGS.LOGIN_METHOD.DESCRIPTION')"
    >
      <div class="flex flex-col gap-3">
        <SettingsChoiceCard
          v-model="loginMethod"
          :value="LOGIN_METHODS.PASSWORD"
          icon="i-ph-envelope-simple"
          :label="t('SECURITY_SETTINGS.LOGIN_METHOD.PASSWORD.LABEL')"
          :description="
            t('SECURITY_SETTINGS.LOGIN_METHOD.PASSWORD.DESCRIPTION')
          "
          :active-label="t('SECURITY_SETTINGS.LOGIN_METHOD.ACTIVE')"
          :disabled="isLoading"
        />
        <SettingsChoiceCard
          v-model="loginMethod"
          :value="LOGIN_METHODS.SAML"
          icon="i-ph-key"
          :label="t('SECURITY_SETTINGS.LOGIN_METHOD.SAML.LABEL')"
          :description="t('SECURITY_SETTINGS.LOGIN_METHOD.SAML.DESCRIPTION')"
          :active-label="t('SECURITY_SETTINGS.LOGIN_METHOD.ACTIVE')"
          :disabled="isLoading"
        />
      </div>
    </SettingsSection>

    <SettingsSection
      v-if="hasFeature && isEnabled && !isLoading"
      flush
      :title="t('SECURITY_SETTINGS.SAML.TITLE')"
      :description="t('SECURITY_SETTINGS.SAML.NOTE')"
    >
      <div class="flex flex-col w-full gap-5">
        <SamlInfoSection
          :fingerprint="fingerprint"
          :sp-entity-id="spEntityId"
        />
        <SamlAttributeMap />

        <form class="flex flex-col gap-5" @submit.prevent="handleSubmit">
          <SettingsFieldGrid>
            <SettingsField
              full-width
              :label="t('SECURITY_SETTINGS.SAML.SSO_URL.LABEL')"
              :help-text="t('SECURITY_SETTINGS.SAML.SSO_URL.HELP')"
              :error-message="ssoUrlError"
            >
              <TextInput
                v-model="formState.ssoUrl"
                class="w-full"
                type="url"
                :placeholder="t('SECURITY_SETTINGS.SAML.SSO_URL.PLACEHOLDER')"
              />
            </SettingsField>

            <SettingsField
              full-width
              :label="t('SECURITY_SETTINGS.SAML.IDP_ENTITY_ID.LABEL')"
              :help-text="t('SECURITY_SETTINGS.SAML.IDP_ENTITY_ID.HELP')"
              :error-message="idpEntityIdError"
            >
              <TextInput
                v-model="formState.idpEntityId"
                class="w-full"
                :placeholder="
                  t('SECURITY_SETTINGS.SAML.IDP_ENTITY_ID.PLACEHOLDER')
                "
              />
            </SettingsField>

            <SettingsField
              full-width
              :label="t('SECURITY_SETTINGS.SAML.CERTIFICATE.LABEL')"
              :help-text="t('SECURITY_SETTINGS.SAML.CERTIFICATE.HELP')"
              :error-message="certificateError"
            >
              <TextArea
                v-model="formState.certificate"
                class="w-full"
                rows="8"
                :placeholder="
                  t('SECURITY_SETTINGS.SAML.CERTIFICATE.PLACEHOLDER')
                "
              />
            </SettingsField>
          </SettingsFieldGrid>

          <div class="flex">
            <NextButton
              type="submit"
              size="sm"
              :is-loading="isSubmitting"
              :label="t('SECURITY_SETTINGS.SAML.UPDATE_BUTTON')"
            />
          </div>
        </form>
      </div>
    </SettingsSection>
  </div>
</template>
